import { useCallback } from 'react';
import { ApiError, api } from './api';
import { useUi } from '@allrouted/ui/Toaster';

/**
 * Taking an order off a route, and deleting it outright.
 *
 * Three screens offer these now -- the order drawer, the dispatch map's bubble
 * and the orders map's bubble -- and they are the two most consequential
 * things a dispatcher can do to a day. The wording of the confirmation, what
 * the toast says afterwards, and whether anything is asked at all are not
 * decisions that should vary by which pixel was clicked, so they live here
 * once.
 */

export interface OrderActions {
  /** Off the route, still on the day. Resolves true when it happened. */
  unassign: (orderId: string, orderName: string, onDate: string) => Promise<boolean>;
  /** Gone entirely, after a confirmation. Resolves true when it happened. */
  remove: (orderId: string, orderName: string) => Promise<boolean>;
}

export function useOrderActions(territoryId: string | null): OrderActions {
  const ui = useUi();

  const unassign = useCallback(
    async (orderId: string, orderName: string, onDate: string) => {
      if (!territoryId) return false;
      try {
        await api.unassignOrder(territoryId, onDate, orderId);
        ui.notify(
          'ok',
          `${orderName} taken off the route`,
          'It stays on this date, unassigned, and the route was retimed.',
        );
        return true;
      } catch (err) {
        ui.notify(
          'error',
          'Could not take it off the route',
          err instanceof ApiError ? err.message : String(err),
        );
        return false;
      }
    },
    [territoryId, ui],
  );

  const remove = useCallback(
    async (orderId: string, orderName: string) => {
      if (!territoryId) return false;
      /*
       * Asked, because there is no undo. The name is in the question: a dialog
       * that says "delete this order?" is answered yes by somebody who has
       * already forgotten which row they clicked -- and from a map bubble they
       * may well have clicked a pin two streets from the one they meant.
       */
      const ok = await ui.confirm({
        title: `Delete ${orderName}?`,
        body: 'The order and its place on any route are removed. This cannot be undone.',
        confirmLabel: 'Delete order',
        danger: true,
      });
      if (!ok) return false;
      try {
        await api.deleteOrder(territoryId, orderId);
        ui.notify('ok', `${orderName} deleted`);
        return true;
      } catch (err) {
        ui.notify(
          'error',
          'Could not delete that order',
          err instanceof ApiError ? err.message : String(err),
        );
        return false;
      }
    },
    [territoryId, ui],
  );

  return { unassign, remove };
}
